import React from 'react';
import './InstallButton.css';
import Modal_pop_up from '../Model_pop_up/Modal_pop_up';

// The beforeinstallprompt event isn't in the standard TS lib yet.
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isIos = (): boolean => {
    const ua = window.navigator.userAgent.toLowerCase();
    // iPadOS 13+ reports as "Macintosh" but is touch-capable.
    const iOSDevice = /iphone|ipad|ipod/.test(ua);
    const iPadOS = ua.includes('macintosh') && 'ontouchend' in document;
    return iOSDevice || iPadOS;
};

const isInStandaloneMode = (): boolean => {
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        // iOS Safari exposes this non-standard flag when launched from home screen.
        (window.navigator as any).standalone === true
    );
};

const InstallButton = () => {
    const [deferredPrompt, setDeferredPrompt] =
        React.useState<BeforeInstallPromptEvent | null>(null);
    const [showButton, setShowButton] = React.useState(false);
    const [showIosHelp, setShowIosHelp] = React.useState(false);

    React.useEffect(() => {
        // Already installed / running as an app: nothing to offer.
        if (isInStandaloneMode()) {
            return;
        }

        // Android / desktop Chromium browsers fire this when the app is installable.
        const onBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            setShowButton(true);
        };

        // Hide the button once the app has been installed.
        const onAppInstalled = () => {
            setDeferredPrompt(null);
            setShowButton(false);
        };

        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        window.addEventListener('appinstalled', onAppInstalled);

        // iOS never fires beforeinstallprompt, so show the button and guide the
        // user through the manual "Add to Home Screen" flow.
        if (isIos()) {
            setShowButton(true);
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
            window.removeEventListener('appinstalled', onAppInstalled);
        };
    }, []);

    const handleClick = async () => {
        if (deferredPrompt) {
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setShowButton(false);
            }
            setDeferredPrompt(null);
            return;
        }

        if (isIos()) {
            setShowIosHelp(true);
        }
    };

    if (!showButton) {
        return null;
    }

    return (
        <>
            <button className="install_button" onClick={handleClick}>
                <span className="install_button_icon" aria-hidden="true">
                    &#8681;
                </span>
                Install App
            </button>

            <Modal_pop_up
                isOpen={showIosHelp}
                title="Install MAT Facilitator"
                body={
                    <div className="install_ios_help">
                        <p>To add this app to your home screen:</p>
                        <ol>
                            <li>
                                Tap the <strong>Share</strong> button in Safari&apos;s
                                toolbar (the square with an arrow pointing up).
                            </li>
                            <li>
                                Scroll down and tap <strong>Add to Home Screen</strong>.
                            </li>
                            <li>
                                Tap <strong>Add</strong> in the top-right corner.
                            </li>
                        </ol>
                    </div>
                }
                buttonTitle="Got it"
                bnt_confirm_onclick={() => setShowIosHelp(false)}
                disable={false}
            />
        </>
    );
};

export default InstallButton;
